<script lang="ts">
  // Die Arbeitsflaeche der Shell (docs/shell.md).
  //
  // Eine oder zwei Spalten, jede mit eigener Tab-Leiste, eigenem
  // Breadcrumb und eigenem Inhalt. Sie ist KEIN Overlay: sie ist die
  // Flaeche, in der die App lebt. Deshalb hat sie weder Backdrop noch
  // Schliessen-Knopf noch role="dialog" -- das war die alte Werkbank,
  // die ueber der App lag.
  //
  // Warum eine eigene Tab-Leiste statt lib/TabBar.svelte: TabBar ist das
  // Primitive fuer Filter und Ansichts-Umschalter -- ihr Vertrag ist
  // {id,label,count,href}. Diese hier sind Dokument-Tabs: schliessbar,
  // mit eigener Identitaet, zwischen Spalten verschiebbar. Andere Sache,
  // kein Sonderfall derselben.

  import { untrack } from "svelte";
  import Griff from "./Griff.svelte";
  import TabAnsicht from "./TabAnsicht.svelte";
  import Icon from "../Icon.svelte";
  import Kreuz from "../Kreuz.svelte";
  import { t } from "../i18n.svelte";
  import { portal } from "../portal";
  import { findRoute } from "../routes";
  import { buildCrumbs } from "../nav-sections";
  import { hatEinstellungen } from "../einstellungen/manifest";
  import { bereichVon, getPanel, iconVon, titelVon, untertitelVon } from "./registry";
  import { gehePfad, oeffnePfad } from "./route-sync.svelte";
  import { meldeTabAufgebaut, starteTabMessung } from "./tab-timing.svelte";
  import { ladeBaustein } from "../baustein";
  import { laeuftGerade } from "../laeuft.svelte";
  import { seitenPfadVon, tabLaeufe } from "../laeuft";
  import {
    aktiviere,
    fokussiere,
    hefteAn,
    anzahlRechts,
    beginntBlock,
    leisteGeordnet,
    oeffnerVon,
    ordneNachBereich,
    schliesseAlle,
    schliesseAndere,
    schliesseRechts,
    schliesseTab,
    setShellSplitPct,
    shellFokus,
    shellGeteilt,
    shellGroups,
    shellSplitPct,
    sichtbarerTab,
    verschiebeTab,
    type ShellTab,
  } from "./tabs.svelte";

  interface Props {
    /** Schmale Fenster bekommen keine zwei Spalten -- nebeneinander
     *  waere beides unlesbar. */
    schmal?: boolean;
  }
  let { schmal = false }: Props = $props();

  const gruppen = $derived(shellGroups());
  const geteilt = $derived(shellGeteilt() && !schmal);
  const fokus = $derived(shellFokus());
  // Was auf welcher Seite gerade arbeitet (Tab-Punkt, .tab-lauf).
  const tabLauf = $derived(tabLaeufe(laeuftGerade()));
  const splitPct = $derived(shellSplitPct());

  const sichtbareGruppen = $derived(geteilt ? [0, 1] : [0]);

  // Ladezeit des Tabs messen (tab-timing.svelte.ts). $effect.PRE, nicht
  // $effect: der Startpunkt muss vor dem Neuaufbau liegen, sonst fehlt
  // gerade das Abraeumen des vorigen Tabs -- bei einer grossen Tabelle
  // der teuerste Teil des Wechsels.
  $effect.pre(() => {
    const g = gruppen[fokus] ?? gruppen[0];
    const id = g?.activeId;
    if (!id) return;
    const tab = g.tabs.find((x) => x.id === id);
    if (!tab) return;
    starteTabMessung(id, titelVon(tab.typ, tab.params));
  });

  // ─── Kontextmenue am Tab ───────────────────────────────────────────
  //
  // Rechtsklick auf einen Tab bietet an, aufzuraeumen. Die Reihenfolge
  // folgt VS Code, der Inhalt aber dieser Shell: "Close Saved" hat hier
  // kein Gegenstueck (es gibt nichts Ungespeichertes) und waere ein
  // Menuepunkt, der so tut als koennte er etwas.
  //
  // Aufbau wie das Kontextmenue der Dateien-Seite: feste Lage am
  // Mauszeiger, data-ctxmenu als Marker fuer den Klick-ausserhalb-
  // Waechter, Escape schliesst.
  let tabMenue = $state<{
    gruppe: number;
    id: string;
    titel: string;
    temporaer: boolean;
    x: number;
    y: number;
  } | null>(null);

  function oeffneTabMenue(e: MouseEvent, gruppe: number, tab: ShellTab): void {
    e.preventDefault();
    e.stopPropagation();
    // Der Tab unter dem Zeiger wird auch der aktive -- sonst bezoege
    // sich "Andere schliessen" auf etwas anderes als das, was man
    // gerade ansieht.
    aktiviere(gruppe, tab.id);
    tabMenue = {
      gruppe,
      id: tab.id,
      titel: titelVon(tab.typ, tab.params),
      temporaer: !!tab.temporaer,
      x: e.clientX,
      y: e.clientY,
    };
  }

  function schliesseTabMenue(): void {
    tabMenue = null;
  }

  // Das Kuerzel je nach Tastatur schreiben. Die Taste ist dieselbe
  // (beiTaste in Shell.svelte prueft metaKey ODER ctrlKey) -- nur heisst
  // sie auf dem Mac anders, und ein "Strg" auf einer Apple-Tastatur
  // schickt einen auf die Suche nach einer Taste, die es dort so nicht
  // gibt.
  const macTastatur =
    typeof navigator !== "undefined" &&
    /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
  const kuerzelSchliessen = macTastatur ? "⇧⌘W" : "Strg+Umschalt+W";

  // Nach dem Ausfuehren immer zu: ein Menue, das stehen bleibt, waehrend
  // sich darunter die Tab-Leiste umbaut, zeigt auf nichts mehr.
  function ausMenue(fn: () => void): void {
    fn();
    schliesseTabMenue();
  }

  const rechtsOffen = $derived(
    tabMenue ? anzahlRechts(tabMenue.gruppe, tabMenue.id) : 0,
  );
  const tabsInGruppe = $derived(
    tabMenue ? (gruppen[tabMenue.gruppe]?.tabs.length ?? 0) : 0,
  );

  function beiFensterKlick(e: MouseEvent): void {
    if (!tabMenue && !ueberlaufMenue) return;
    const ziel = e.target as HTMLElement | null;
    // Klicks IM Menue haben ihren eigenen Handler schon ausgefuehrt.
    if (ziel?.closest("[data-ctxmenu]")) return;
    schliesseTabMenue();
    ueberlaufMenue = null;
  }

  function beiFensterTaste(e: KeyboardEvent): void {
    if (e.key !== "Escape") return;
    if (tabMenue) schliesseTabMenue();
    if (ueberlaufMenue) ueberlaufMenue = null;
  }

  // Fertig-Meldung fuer alles, was KEINE Seite ist (Terminal, Diff, ...):
  // deren Inhalt steht mit diesem Durchlauf. Seiten-Tabs melden sich
  // selbst aus PageHost, denn dort haengt hinter dem Wirt noch der
  // Lazy-Chunk der Seite -- hier waere die Meldung zu frueh.
  $effect(() => {
    const g = gruppen[fokus] ?? gruppen[0];
    const id = g?.activeId;
    if (!id) return;
    const tab = g.tabs.find((x) => x.id === id);
    if (!tab || tab.typ === "page") return;
    meldeTabAufgebaut();
  });

  // Bei einer Spalte zeigen wir die Tabs BEIDER Gruppen in einer Leiste,
  // sonst waeren die der rechten Spalte unerreichbar (schmales Fenster).
  const leisteEinspaltig = $derived.by(() => {
    if (geteilt) return null;
    const alle: { gruppe: number; tab: ShellTab }[] = [];
    gruppen.forEach((g, i) => g.tabs.forEach((tab) => alle.push({ gruppe: i, tab })));
    return alle;
  });

  function anzuzeigenderTab(gruppe: number): ShellTab | null {
    if (geteilt) return sichtbarerTab(gruppe);
    // Einspaltig: der fokussierte Tab gewinnt, egal aus welcher Gruppe.
    return sichtbarerTab(fokus) ?? sichtbarerTab(0) ?? sichtbarerTab(1);
  }

  // ─── Gehaltene Tabs ────────────────────────────────────────────────
  //
  // Tim 18.10.2026: „Lassen sich die offenen Tabs im RAM halten, damit dann
  // nur noch die Daten beim Fokus geladen werden müssen und nicht der
  // Inhalt selbst?" Je Spalte bleiben die zuletzt gezeigten Tabs gebaut
  // und liegen verdeckt unter dem vorderen (content-visibility: hidden --
  // kein Layout, kein Zeichnen, der Rollstand bleibt). Der Wechsel zeigt
  // sie nur wieder; ihre Daten laufen über die Summary-Stores weiter.
  // Höchstens HALTEN je Spalte: wer am längsten nicht vorn war, wird
  // abgebaut. Was nur vorn laufen soll, fragt seiteVorn (page-route.ts).
  const HALTEN = 8;
  let zuletzt = $state<string[][]>([[], []]);
  const schluesselVon = (tab: ShellTab) => tab.schluessel ?? tab.id;

  $effect.pre(() => {
    const neu = sichtbareGruppen.map((g) => {
      const vorn = anzuzeigenderTab(g);
      const alle = new Set(gruppen.flatMap((x) => x.tabs.map(schluesselVon)));
      const liste = untrack(() => zuletzt[g] ?? []).filter((k) => alle.has(k));
      if (!vorn) return liste;
      const k = schluesselVon(vorn);
      // Stand er schon gebaut da, ist der Wechsel fertig, sobald er sich
      // zeigt (tab-timing: sonst wartete die Messung auf einen Aufbau,
      // der nicht kommt).
      if (liste.includes(k) && liste[0] !== k) queueMicrotask(() => meldeTabAufgebaut());
      return [k, ...liste.filter((x) => x !== k)].slice(0, HALTEN);
    });
    untrack(() => {
      if (neu.some((l, i) => l.join("|") !== (zuletzt[i] ?? []).join("|"))) zuletzt = [...neu, ...zuletzt.slice(neu.length)];
    });
  });

  // Verdeckt hat die rollende Fläche kein Layout (content-visibility), der
  // Browser setzt ihren Stand auf 0 (Tim 18.10.2026: „man muss jedes Mal
  // die Scrollposition wieder suchen"). Darum merkt sich jede Fläche, wo
  // sie vorn stand -- nur aus eigenem Rollen, nicht aus dem Zurücksetzen
  // beim Verdecken -- und kehrt dorthin zurück, sobald sie wieder vorn ist.
  function rollstand(node: HTMLElement, vorn: boolean) {
    let oben = node.scrollTop;
    let istVorn = vorn;
    const merke = () => {
      if (istVorn) oben = node.scrollTop;
    };
    node.addEventListener("scroll", merke, { passive: true });
    return {
      update(neu: boolean) {
        if (neu && !istVorn) {
          istVorn = true;
          requestAnimationFrame(() => {
            node.scrollTop = oben;
          });
        } else if (!neu) {
          istVorn = false;
        }
      },
      destroy() {
        node.removeEventListener("scroll", merke);
      },
    };
  }

  /** Die gebauten Tabs einer Spalte -- der vordere und die gehaltenen. */
  function gehalten(g: number, vorn: ShellTab | null): ShellTab[] {
    const nachSchluessel = new Map(gruppen.flatMap((x) => x.tabs.map((t) => [schluesselVon(t), t] as const)));
    const aus: ShellTab[] = [];
    for (const k of zuletzt[g] ?? []) {
      const t = nachSchluessel.get(k);
      if (t) aus.push(t);
    }
    if (vorn && !aus.some((t) => schluesselVon(t) === schluesselVon(vorn))) aus.unshift(vorn);
    return aus;
  }

  function gruppeVonTab(tab: ShellTab | null): number {
    if (!tab) return 0;
    return gruppen.findIndex((g) => g.tabs.some((x) => x.id === tab.id));
  }

  /** hatParameter: traegt die Route Platzhalter (/zuhause/aktoren/:name)?
   *  Dann benennt der Tab-Titel das Ding, sonst reicht das Route-Label. */
  function hatParameter(pfad: string): boolean {
    return Object.keys(findRoute(pfad)?.params ?? {}).length > 0;
  }

  /** oeffneEinstellungen legt das Einstellungsfenster NEBEN die Seite,
   *  nicht an ihre Stelle: Stellschraube und Wirkung gleichzeitig sehen.
   *  Liegt der Tab schon rechts, geht es nach links. */
  function oeffneEinstellungen(vonGruppe: number) {
    oeffnePfad("/einstellungen", { gruppe: vonGruppe === 0 ? 1 : 0 });
  }

  /** Bezugsrahmen fuer die Spalten-Teilung: der Griff meldet nur die
   *  Zeigerposition, den Anteil rechnen wir daraus. */
  let flaecheEl = $state<HTMLElement | null>(null);

  // Mittelklick schliesst einen Tab -- in jedem Editor so, und es spart
  // das Zielen auf das kleine Kreuz.
  function beiMausklick(e: MouseEvent, gruppe: number, id: string) {
    if (e.button === 1) {
      e.preventDefault();
      schliesseTab(gruppe, id);
    }
  }

  // ── Tabs ziehen ───────────────────────────────────────────────────
  //
  // Bewusst die native HTML-Drag-API statt Pointer-Events von Hand:
  // sie bringt Aufheben, Cursor, Abbruch per Esc und das Fallenlassen
  // ausserhalb des Fensters schon mit. Was wir selbst tun muessen, ist
  // die MARKE zu zeigen -- wohin faellt der Tab? Ohne die zieht man ins
  // Ungewisse.
  //
  // Der Zustand liegt im Modul (nicht im DataTransfer), weil Firefox
  // waehrend dragover keine Daten herausgibt und die Einfuegemarke
  // damit nicht berechnet werden koennte.
  let zug = $state<{ gruppe: number; id: string } | null>(null);
  let marke = $state<{ gruppe: number; index: number } | null>(null);
  let teilenZone = $state(false);

  function starteTabZug(e: DragEvent, gruppe: number, id: string) {
    zug = { gruppe, id };
    e.dataTransfer?.setData("text/plain", id);
    if (e.dataTransfer) e.dataTransfer.effectAllowed = "move";
  }
  function endeTabZug() {
    zug = null;
    marke = null;
    teilenZone = false;
  }

  /** einfuegeStelle rechnet aus der Mausposition die Ziel-Position in
   *  der Leiste aus: vor dem Tab, ueber dessen linker Haelfte man ist. */
  function einfuegeStelle(e: DragEvent, leiste: HTMLElement): number {
    const tabs = [...leiste.querySelectorAll<HTMLElement>("[data-tab-id]")];
    for (let i = 0; i < tabs.length; i++) {
      const r = tabs[i].getBoundingClientRect();
      if (e.clientX < r.left + r.width / 2) return i;
    }
    return tabs.length;
  }

  function ueberLeiste(e: DragEvent, gruppe: number) {
    if (!zug) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
    marke = { gruppe, index: einfuegeStelle(e, e.currentTarget as HTMLElement) };
    teilenZone = false;
  }

  function fallenLassen(e: DragEvent, gruppe: number) {
    if (!zug) return;
    e.preventDefault();
    const stelle = einfuegeStelle(e, e.currentTarget as HTMLElement);
    verschiebeTab(zug.gruppe, zug.id, gruppe, stelle);
    endeTabZug();
  }

  // Rechte Kante der EINEN Spalte = "hier aufmachen": das ist die Geste
  // fuer die geteilte Ansicht, ohne vorher einen Knopf zu suchen.
  function ueberTeilenZone(e: DragEvent) {
    if (!zug) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
    teilenZone = true;
    marke = null;
  }
  function fallenLassenTeilen(e: DragEvent) {
    if (!zug) return;
    e.preventDefault();
    verschiebeTab(zug.gruppe, zug.id, 1);
    endeTabZug();
  }

  // ── Wenn die Tabs nicht mehr passen ────────────────────────────────
  //
  // Mockup: docs/mockups/tabs-ueberlauf.html. Drei Stufen, in dieser
  // Reihenfolge:
  //
  //   1. Die Tabs geben Breite ab, bis 7,5rem erreicht sind (CSS unten).
  //   2. Danach scrollt die Leiste. Eine Verlaufskante zeigt die Seite,
  //      an der noch etwas liegt -- vorher war die einzige Andeutung ein
  //      Tab, der mitten im Wort abgeschnitten war.
  //   3. Was nicht GANZ im Bild steht, zaehlt ein Knopf rechts und
  //      listet ein Menue auf. Erst das macht die Zusage, dass kein Tab
  //      unauffindbar ist -- auch der zwanzigste nicht.
  //
  // Dazu die Regel, die unabhaengig von der Darstellung gilt und die
  // eigentliche Luecke war: der AKTIVE Tab ist nie verdeckt.
  //
  // Bewusst NICHT ueber display:none (so stand es im Mockup): Verstecken
  // gibt Platz frei, der freie Platz laesst den naechsten Tab wieder
  // passen -- eine Messung, die ihre eigene Grundlage veraendert,
  // schwingt. Und um den aktiven Tab hereinzuholen, muesste sie die
  // Reihenfolge umsortieren; die gehoert dem Benutzer (Ziehen) und liegt
  // in den Prefs, also auf allen Geraeten zugleich.

  /** Der Scrollport je Gruppe -- Messpunkt UND Scrollziel. */
  let leisteEls = $state<(HTMLElement | null)[]>([null, null]);
  /** Tab-IDs, die nicht ganz im Bild stehen. Quelle fuer Zahl + Menue. */
  let verdeckt = $state<string[][]>([[], []]);
  let kanteLinks = $state([false, false]);
  let kanteRechts = $state([false, false]);
  let ueberlaufMenue = $state<{ gruppe: number; x: number; y: number } | null>(null);

  function messeLeiste(g: number): void {
    const port = leisteEls[g];
    if (!port) return;
    const rand = port.getBoundingClientRect();
    const ids: string[] = [];
    for (const el of port.querySelectorAll<HTMLElement>("[data-tab-id]")) {
      const r = el.getBoundingClientRect();
      // Ganz oder gar nicht: ein halb angeschnittener Tab ist kein
      // Angebot, sondern eine Andeutung -- er gehoert in die Liste.
      if (r.left < rand.left - 1 || r.right > rand.right + 1) {
        ids.push(el.dataset.tabId!);
      }
    }
    // Nur bei echter Aenderung schreiben: das hier haengt am
    // scroll-Ereignis, und ein neues Array bei jedem Pixel wuerde die
    // Leiste bei jedem Wisch neu zeichnen lassen.
    const alt = verdeckt[g] ?? [];
    if (alt.length !== ids.length || ids.some((id, i) => alt[i] !== id)) {
      verdeckt[g] = ids;
    }
    kanteLinks[g] = port.scrollLeft > 1;
    kanteRechts[g] = port.scrollLeft + port.clientWidth < port.scrollWidth - 1;
  }

  /** Scrollt die Leiste, bis der aktive Tab ganz im Bild steht.
   *
   *  Bewusst von Hand statt scrollIntoView(): das scrollt JEDEN
   *  Vorfahren mit, also auch die Arbeitsflaeche und das Fenster. Im
   *  Mockup ist die Seite dadurch beim Laden an den letzten Pruefstand
   *  gesprungen -- hier waere es die halbe App, weil ein Tab aktiv
   *  wurde. Eine Leiste darf nur sich selbst bewegen. */
  function haltAktivenImBlick(g: number): void {
    const port = leisteEls[g];
    if (!port) return;
    const el = port.querySelector<HTMLElement>("[data-tab-id].active");
    if (!el) return;
    const rand = port.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    if (r.left < rand.left) port.scrollLeft -= rand.left - r.left;
    else if (r.right > rand.right) port.scrollLeft += r.right - rand.right;
  }

  function pruefeLeisten(): void {
    for (const g of [0, 1]) {
      if (!sichtbareGruppen.includes(g)) {
        // Aufgeraeumt: sonst blitzt beim naechsten Teilen kurz die Zahl
        // von vorhin auf, bevor der erste Messpunkt da ist.
        if (verdeckt[g].length) verdeckt[g] = [];
        continue;
      }
      haltAktivenImBlick(g);
      messeLeiste(g);
    }
  }

  // Nachmessen, wenn sich der Stapel aendert. Erst im naechsten Bild:
  // vorher steht das Layout noch nicht, und gemessen wuerde der
  // vorherige Zustand.
  $effect(() => {
    // Abhaengigkeiten ausdruecklich anmelden -- gemessen wird im DOM,
    // und das sieht Svelte nicht.
    void gruppen.map((g) => `${g.tabs.length}:${g.activeId}`).join("|");
    void geteilt;
    void splitPct;
    void leisteEls[0];
    void leisteEls[1];
    const bild = requestAnimationFrame(pruefeLeisten);
    return () => cancelAnimationFrame(bild);
  });

  // Und wenn sich die Breite aendert: schmaler wird das Fenster ohne
  // Zutun (Split-Griff, Seitenleiste, Panel), und dann stimmt die Zahl
  // im Knopf sonst still nicht mehr.
  $effect(() => {
    const obs = new ResizeObserver(() => pruefeLeisten());
    for (const g of sichtbareGruppen) {
      const el = leisteEls[g];
      if (el) obs.observe(el);
    }
    return () => obs.disconnect();
  });

  /** Die verdeckten Tabs als Eintraege -- einspaltig stehen die Tabs
   *  BEIDER Gruppen in einer Leiste, die Gruppe muss also mitgefuehrt
   *  werden, sonst aktiviert das Menue in der falschen. */
  function verdeckteEintraege(g: number): { gruppe: number; tab: ShellTab }[] {
    const liste = geteilt
      ? (gruppen[g]?.tabs ?? []).map((tab) => ({ gruppe: g, tab }))
      : (leisteEinspaltig ?? []);
    const ids = new Set(verdeckt[g] ?? []);
    return liste.filter((e) => ids.has(e.tab.id));
  }

  function oeffneUeberlauf(e: MouseEvent, gruppe: number): void {
    e.stopPropagation();
    if (ueberlaufMenue?.gruppe === gruppe) {
      ueberlaufMenue = null;
      return;
    }
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    ueberlaufMenue = { gruppe, x: r.right, y: r.bottom + 4 };
  }

  function ausUeberlauf(gruppe: number, id: string): void {
    aktiviere(gruppe, id);
    // Zu, wie beim Kontextmenue: die Liste beschreibt einen Zustand, den
    // der Klick gerade aufgehoben hat.
    ueberlaufMenue = null;
  }
</script>

<div class="kasten border-border relative flex min-h-0 flex-1 overflow-hidden rounded-md border" bind:this={flaecheEl}>
  {#each sichtbareGruppen as g (g)}
    {@const tab = anzuzeigenderTab(g)}
    {@const tabs = geteilt ? gruppen[g].tabs.map((x) => ({ gruppe: g, tab: x })) : (leisteEinspaltig ?? [])}
    {@const aktivId = geteilt ? gruppen[g].activeId : (tab?.id ?? null)}
    <section
      class="flex min-h-0 min-w-0 flex-col"
      class:border-l={geteilt && g === 1}
      class:border-border={geteilt && g === 1}
      style={geteilt && g === 0 ? `flex: 0 0 ${splitPct}%` : "flex: 1"}
      onfocusin={() => fokussiere(g)}
    >
      <!-- ══ Tab-Leiste ═══════════════════════════════════════════ -->
      <!-- role="tablist" + tabindex="-1": der Streifen IST die Tab-Leiste
           (und nimmt als Ablegeziel Zuege entgegen), aber er fuehrt
           keinen eigenen Fokus -- das tun die Tabs darin. -->
      <div
        class="tabstrip flex items-stretch"
        role="tablist"
        tabindex="-1"
        ondragover={(e) => ueberLeiste(e, g)}
        ondrop={(e) => fallenLassen(e, g)}
      >
        <!-- Der Port traegt die Verlaufskanten: sie muessen STEHEN
             bleiben, waehrend die Tabs darunter durchlaufen. Im
             Scroll-Container selbst wuerden sie mitscrollen. -->
        <div class="tabs-port relative flex min-w-0 flex-1">
          <div
            class="tabs flex min-w-0 flex-1 items-stretch"
            bind:this={leisteEls[g]}
            onscroll={() => messeLeiste(g)}
          >
            {#each tabs as eintrag, i (eintrag.tab.id)}
              {@const ident = eintrag.tab.params as Record<string, unknown>}
              {@const icon = iconVon(eintrag.tab.typ, ident)}
              {@const lauf = eintrag.tab.typ === "page" && typeof ident.path === "string" ? tabLauf.get(seitenPfadVon(ident.path)) : undefined}
              {#if marke?.gruppe === g && marke.index === i}
                <div class="einfuegemarke" aria-hidden="true"></div>
              {/if}
              <div
                class="tab"
                class:blockanfang={beginntBlock(eintrag.gruppe, eintrag.tab.id)}
                class:active={eintrag.tab.id === aktivId}
                class:fokussiert={eintrag.tab.id === aktivId && eintrag.gruppe === fokus}
                class:vorschau={eintrag.tab.temporaer}
                class:zieht={zug?.id === eintrag.tab.id}
                role="tab"
                tabindex="0"
                draggable="true"
                data-tab-id={eintrag.tab.id}
                data-bereich={bereichVon(eintrag.tab.typ, ident)}
                aria-selected={eintrag.tab.id === aktivId}
                title={untertitelVon(eintrag.tab.typ, ident) ?? undefined}
                onclick={() => aktiviere(eintrag.gruppe, eintrag.tab.id)}
                ondblclick={() => hefteAn(eintrag.gruppe, eintrag.tab.id)}
                oncontextmenu={(e) => oeffneTabMenue(e, eintrag.gruppe, eintrag.tab)}
                onauxclick={(e) => beiMausklick(e, eintrag.gruppe, eintrag.tab.id)}
                ondragstart={(e) => starteTabZug(e, eintrag.gruppe, eintrag.tab.id)}
                ondragend={endeTabZug}
                onkeydown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    aktiviere(eintrag.gruppe, eintrag.tab.id);
                  }
                }}
              >
                {#if icon}<Icon name={icon} size={13} class="tab-symbol" />{/if}
                <span class="titel truncate">{titelVon(eintrag.tab.typ, ident)}</span>
                {#if lauf}
                  <span class="tab-lauf {lauf.ton}" title={lauf.titel} role="img" aria-label={lauf.titel}></span>
                {/if}
                <button
                  type="button"
                  class="tab-x"
                  aria-label={t("shell.tab_close", "Tab schließen")}
                  onclick={(e) => {
                    e.stopPropagation();
                    schliesseTab(eintrag.gruppe, eintrag.tab.id);
                  }}
                >
                  <Kreuz />
                </button>
              </div>
            {/each}
            {#if marke?.gruppe === g && marke.index >= tabs.length}
              <div class="einfuegemarke" aria-hidden="true"></div>
            {/if}
          </div>
          <div class="kante links" class:an={kanteLinks[g]} aria-hidden="true"></div>
          <div class="kante rechts" class:an={kanteRechts[g]} aria-hidden="true"></div>
        </div>

        {#if (verdeckt[g]?.length ?? 0) > 0}
          <!-- Die Zahl ist der Punkt: "da ist noch etwas" weiss man auch
               ohne sie, "da sind noch siebzehn" nicht. -->
          <button
            type="button"
            class="strip-btn"
            aria-haspopup="menu"
            aria-expanded={ueberlaufMenue?.gruppe === g}
            title={`${verdeckt[g].length} ${t("shell.tabs_hidden", "weitere Tabs")}`}
            aria-label={`${verdeckt[g].length} ${t("shell.tabs_hidden", "weitere Tabs")}`}
            onclick={(e) => oeffneUeberlauf(e, g)}
          >
            <Icon name="chevron-down" size={14} />
            <span class="zahl tabular-nums">{verdeckt[g].length}</span>
          </button>
        {/if}

        {#if !schmal}
          <!-- Kein Knopf fuer "in die andere Spalte" und keiner fuers
               Teilen: beides macht Ziehen (Tab an die rechte Kante =
               "Hier teilen", Tab in die Nachbarleiste = verschieben),
               und Strg+\ gibt es weiter. Zwei Wege fuer dieselbe Geste
               sind zwei Stellen zum Pflegen (Tim 15.08.). -->
        {/if}
      </div>

      <!-- ══ Breadcrumb ═══════════════════════════════════════════ -->
      <!-- Die Leiste ist ein PFAD: sie sagt, wo man ist, und bringt
           einen mit einem Klick eine Ebene zurueck. Wo es diese Ebene
           nicht gibt -- Dashboard, Einstellungen, Profil liegen ganz
           oben -- bleibt nur der Tab-Titel ein zweites Mal stehen,
           unanklickbar. Dort faellt sie weg (Tim 18.08.). Ueberall
           sonst bleibt sie, auch wenn das letzte Glied den Tab
           wiederholt: dann traegt der Weg davor die Information. -->
      {#if tab}
        {@const ident = tab.params as Record<string, unknown>}
        {@const crumbs =
          tab.typ === "page" ? buildCrumbs(String(ident.path ?? "/")) : []}
        <!-- Ein Tab ohne eigene Adresse (Dokument, Container) hat keinen
             Pfad nach oben -- aber einen Ort, von dem man kam. Der steht
             als erstes Glied da und fuehrt zurueck (Tim 07.10.2026, am
             offenen Dokument: „Hier geht es dann nicht mehr zurück").
             Zurück SCHLIESST dabei den Tab: nur hinüberwechseln kann man
             auch mit einem Klick auf den anderen Tab. -->
        {@const oeffner = tab.typ === "page" ? null : oeffnerVon(tab)}
        {@const zeigePfad =
          tab.typ === "page"
            ? crumbs.length > 1 && !findRoute(String(ident.path ?? "/"))?.route.eigenerPfad
            : !!oeffner || !!untertitelVon(tab.typ, ident)}
        <!-- Seiten-Einstellungen: ein Zahnrad rechts in DIESER Zeile, und
             nur wo es sie gibt (Tim 19.09.). Es gehoert zur Seite, nicht
             in die Tab-Leiste; das Zahnrad der Aktivitaetsleiste bleibt
             der globale Einstieg. Hat eine Seite Einstellungen, aber
             keinen Pfad (ganz oben), steht die Zeile trotzdem -- nur mit
             dem Zahnrad. -->
        {@const seitenEinstellungen =
          tab.typ === "page" && hatEinstellungen(String(ident.path ?? ""))}
        {#if zeigePfad || seitenEinstellungen}
        <div class="crumbs border-border text-fg-subtle flex flex-wrap items-center gap-1 border-b px-3">
          {#if tab.typ === "page"}
            {#each crumbs as c, i (c.label + i)}
              {#if i > 0}<span aria-hidden="true" class="opacity-50">›</span>{/if}
              {#if c.href && !c.leaf}
                <button type="button" class="crumb" onclick={() => gehePfad(c.href!)}>
                  {c.label}
                </button>
              {:else}
                <!-- Das letzte Glied benennt das DING, nicht seine Gattung:
                     buildCrumbs kennt nur das Route-Label ("Aktor"), der
                     Tab-Titel kennt den Namen ("Charger Bedroom").
                     NUR bei Param-Routen: bei einem Register wuerde der
                     Tab-Titel die Seite wiederholen ("Storage › Storage"
                     statt "Storage › Alarme"). -->
                <span class="text-fg-muted">
                  {i === crumbs.length - 1 && hatParameter(String(ident.path ?? ""))
                    ? titelVon(tab.typ, ident)
                    : c.label}
                </span>
              {/if}
            {/each}
          {:else}
            {#if oeffner}
              <button
                type="button"
                class="crumb inline-flex items-center gap-1"
                title={t("shell.zurueck_zu", "Schließen und zurück zu „{name}“").replace("{name}", titelVon(oeffner.typ, oeffner.params))}
                onclick={() => schliesseTab(gruppeVonTab(tab), tab.id)}
              >
                <Icon name="chevron-left" size={12} />
                {titelVon(oeffner.typ, oeffner.params)}
              </button>
              <span aria-hidden="true" class="opacity-50">›</span>
            {/if}
            <span class="text-fg-muted">{titelVon(tab.typ, ident)}</span>
            {#if untertitelVon(tab.typ, ident)}
              <span aria-hidden="true" class="opacity-50">·</span>
              <span class="truncate">{untertitelVon(tab.typ, ident)}</span>
            {/if}
          {/if}
          {#if seitenEinstellungen}
            <button
              type="button"
              class="crumb ml-auto inline-flex items-center"
              title={t("shell.page_settings", "Einstellungen dieser Seite daneben öffnen")}
              aria-label={t("shell.page_settings", "Einstellungen dieser Seite daneben öffnen")}
              onclick={() => oeffneEinstellungen(g)}
            >
              <Icon name="settings" size={13} />
            </button>
          {/if}
        </div>
        {/if}
      {/if}

      <!-- ══ Inhalt ═══════════════════════════════════════════════ -->
      <!-- Die Polsterung sitzt HIER, nicht in den Seiten: vorher kam sie
           vom zentrierten Seiten-Wrapper (safe-area-shell + max-w), den
           es in der Shell nicht mehr gibt. Bewusst ohne max-width und
           ohne Zentrierung -- eine Spalte in einem Editor nutzt ihre
           Breite, und bei geteilter Ansicht waere zentrierter Inhalt in
           einer schmalen Spalte doppelt schlecht.
           overflow-x-clip als Layout-Wache gegen lange Token; der
           Karten-Halo darf trotzdem atmen, daher kein overflow-clip auf
           beiden Achsen. -->
      <!-- data-bereich: die Seite erbt die Farbe IHRES Bereichs (app.css
           „Farbe") -- bei geteilter Ansicht jede Spalte ihre eigene. -->
      <!-- @container/seite: Raster der Seite richten sich nach der Breite
           DIESER Spalte (`@lg/seite:grid-cols-2`), nicht nach dem Fenster --
           in der geteilten Ansicht ist die Spalte schmal, das Fenster breit
           (docs/gestaltung.md, Prüfliste 9). -->
      <!-- Gehaltene Tabs (Script, „Gehaltene Tabs"): jeder in seiner eigenen
           rollenden Fläche, die verdeckten darunter -- so behält jeder
           seinen Rollstand. -->
      <div class="stapel relative flex min-h-0 flex-1 flex-col">
        {#each gehalten(g, tab) as h (schluesselVon(h))}
          {@const vorn = !!tab && schluesselVon(h) === schluesselVon(tab)}
          <div
            class="inhalt @container/seite min-h-0 flex-1 overflow-y-auto overflow-x-clip"
            class:verdeckt={!vorn}
            use:rollstand={vorn}
            inert={!vorn}
            aria-hidden={vorn ? undefined : "true"}
            data-bereich={bereichVon(h.typ, h.params as Record<string, unknown>)}
          >
            <div class="inhalt-innen">
              <TabAnsicht {vorn}>
                {@const def = getPanel(h.typ)}
                {#if def}
                  {#await ladeBaustein(`panel ${h.typ}`, def.load)}
                    <p class="text-fg-subtle t-body p-6">{t("common.loading", "Laden…")}</p>
                  {:then mod}
                    {@const Panel = mod.default}
                    <!-- Der Panel bekommt seine Identitaet als Eigenschaften.
                         Dazu onclose: ein Panel muss sich selbst beenden
                         koennen, wenn sein Gegenstand verschwindet (etwa ein
                         entfernter Container). Wer das nicht braucht,
                         ignoriert die Eigenschaft. -->
                    <Panel
                      {...h.params}
                      onclose={() => schliesseTab(gruppeVonTab(h), h.id)}
                    />
                  {:catch err}
                    <p class="text-danger t-body p-6">
                      {t("shell.load_failed", "Inhalt konnte nicht geladen werden")}: {err.message}
                    </p>
                  {/await}
                {/if}
              </TabAnsicht>
            </div>
          </div>
        {/each}
        {#if !tab}
          <div class="inhalt @container/seite min-h-0 flex-1 overflow-y-auto overflow-x-clip">
            <div class="inhalt-innen">
              <!-- Leere Spalte: kein Fehler, sondern ein Angebot. -->
              <div class="grid h-full place-items-center p-8">
                <div class="text-center">
                  <p class="t-body text-fg-muted">
                    {t("shell.empty", "Keine geöffnete Ansicht.")}
                  </p>
                  <button
                    type="button"
                    class="border-border-strong surface text-fg-muted hover:text-fg t-meta mt-3 rounded-lg border px-3 py-1.5 transition"
                    onclick={() => oeffnePfad("/")}
                  >
                    {t("shell.open_start", "Startseite öffnen")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        {/if}
      </div>
    </section>

    {#if geteilt && g === 0}
      <!-- Die Teilung ist eine Linie im selben Kasten: keine Punkte
           (Tim 18.10.2026, nur zwischen zwei Kästen). -->
      <Griff
        achse="x"
        punkte={false}
        label={t("shell.gutter", "Spaltenbreite ändern")}
        wert={Math.round(splitPct)}
        min={20}
        max={80}
        onzug={(pos) => {
          if (!flaecheEl) return;
          const r = flaecheEl.getBoundingClientRect();
          setShellSplitPct(((pos.x - r.left) / r.width) * 100);
        }}
        onschritt={(d) => setShellSplitPct(splitPct + (d > 0 ? 4 : -4))}
      />
    {/if}
  {/each}


  {#if zug && !geteilt && !schmal}
    <!-- Erscheint NUR waehrend eines Zugs: eine dauerhaft sichtbare
         Zone waere Zierrat, hier ist sie eine Antwort auf "wo kann ich
         das hinlegen?". Deshalb auch mit Beschriftung -- ein blosser
         Streifen laesst raten. -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div
      class="teilen-zone"
      class:aktiv={teilenZone}
      ondragover={ueberTeilenZone}
      ondragleave={() => (teilenZone = false)}
      ondrop={fallenLassenTeilen}
    >
      <span class="zone-text">{t("shell.drop_split", "Hier teilen")}</span>
    </div>
  {/if}
</div>

<svelte:window onclick={beiFensterKlick} onkeydown={beiFensterTaste} />

{#if ueberlaufMenue}
  {@const eintraege = verdeckteEintraege(ueberlaufMenue.gruppe)}
  <!-- Rechtsbuendig unter dem Zaehler: der Knopf sitzt an der rechten
       Kante, ein linksbuendiges Menue liefe aus dem Fenster.
       use:portal aus demselben Grund wie beim Kontextmenue -- in der
       Arbeitsflaeche waere es unsichtbar (lib/portal.ts). -->
  {@const breite = 260}
  <div
    use:portal
    data-ctxmenu
    class="border-border bg-bg-elev/95 fixed z-50 max-h-80 overflow-y-auto rounded-xl border p-1 shadow-xl backdrop-blur-xl"
    role="menu"
    tabindex="-1"
    style="left: {Math.max(8, Math.min(ueberlaufMenue.x - breite, window.innerWidth - breite - 8))}px; top: {ueberlaufMenue.y}px; width: {breite}px;"
  >
    <div class="text-fg-subtle t-eyebrow px-2.5 pb-1 pt-1.5">
      {t("shell.tabs_hidden_title", "Nicht im Bild")}
    </div>
    {#each eintraege as eintrag (eintrag.tab.id)}
      {@const ident = eintrag.tab.params as Record<string, unknown>}
      {@const icon = iconVon(eintrag.tab.typ, ident)}
      <button
        type="button"
        role="menuitem"
        class="menuepunkt"
        class:kursiv={eintrag.tab.temporaer}
        onclick={() => ausUeberlauf(eintrag.gruppe, eintrag.tab.id)}
      >
        {#if icon}<Icon name={icon} size={13} />{/if}
        <span class="truncate">{titelVon(eintrag.tab.typ, ident)}</span>
      </button>
    {/each}
  </div>
{/if}

{#if tabMenue}
  <!-- Am Zeiger, aber innerhalb des Fensters: an der rechten Kante
       liefe das Menue sonst hinaus, und die letzten Tabs sitzen genau
       dort. Die Breite ist bekannt (min-width unten), die Hoehe
       schaetzen wir aus der Zahl der Eintraege. -->
  {@const breite = 232}
  {@const hoehe = tabMenue.temporaer ? 232 : 190}
  {@const x = Math.min(tabMenue.x, window.innerWidth - breite - 8)}
  {@const y = Math.min(tabMenue.y, window.innerHeight - hoehe - 8)}
  <!-- use:portal ist hier PFLICHT, nicht Kosmetik: an seiner Stelle im
       Baum lag das Menue zwar korrekt im Layout (getBoundingClientRect
       stimmte), war aber unsichtbar -- die Arbeitsflaeche darueber
       gewinnt den Stapel, und `position: fixed` haengt an einem
       Vorfahren mit eigenem Containing-Block. An <body> gehaengt liegt
       es ueber allem (lib/portal.ts). -->
  <div
    use:portal
    data-ctxmenu
    class="border-border bg-bg-elev/95 fixed z-50 min-w-58 rounded-xl border p-1 shadow-xl backdrop-blur-xl"
    role="menu"
    tabindex="-1"
    style="left: {Math.max(8, x)}px; top: {Math.max(8, y)}px;"
  >
    <div class="text-fg-subtle t-eyebrow truncate px-2.5 pb-1 pt-1.5">
      {tabMenue.titel}
    </div>
    <button
      type="button"
      role="menuitem"
      class="menuepunkt"
      onclick={() => ausMenue(() => schliesseTab(tabMenue!.gruppe, tabMenue!.id))}
    >
      <span>{t("shell.tabmenu.close", "Schließen")}</span>
      <kbd class="text-fg-subtle ml-auto t-meta">{kuerzelSchliessen}</kbd>
    </button>
    <button
      type="button"
      role="menuitem"
      class="menuepunkt"
      disabled={tabsInGruppe < 2}
      onclick={() => ausMenue(() => schliesseAndere(tabMenue!.gruppe, tabMenue!.id))}
    >
      <span>{t("shell.tabmenu.close_others", "Andere schließen")}</span>
    </button>
    <button
      type="button"
      role="menuitem"
      class="menuepunkt"
      disabled={rechtsOffen === 0}
      onclick={() => ausMenue(() => schliesseRechts(tabMenue!.gruppe, tabMenue!.id))}
    >
      <span>{t("shell.tabmenu.close_right", "Rechts davon schließen")}</span>
      {#if rechtsOffen > 0}
        <span class="text-fg-subtle ml-auto t-meta tabular-nums">{rechtsOffen}</span>
      {/if}
    </button>
    <button
      type="button"
      role="menuitem"
      class="menuepunkt"
      onclick={() => ausMenue(() => schliesseAlle(tabMenue!.gruppe))}
    >
      <span>{t("shell.tabmenu.close_all", "Alle schließen")}</span>
      <span class="text-fg-subtle ml-auto t-meta tabular-nums">{tabsInGruppe}</span>
    </button>
    <!-- Die Leiste ordnet sich von selbst nach Bereich; dieser Punkt
         stellt die Ordnung wieder her, nachdem man Tabs von Hand
         verschoben hat (die bleiben sonst liegen). -->
    <hr class="border-border my-1" />
    <button
      type="button"
      role="menuitem"
      class="menuepunkt"
      disabled={leisteGeordnet(tabMenue.gruppe)}
      onclick={() => ausMenue(() => ordneNachBereich(tabMenue!.gruppe))}
    >
      <span>{t("shell.tabmenu.sort", "Nach Bereich ordnen")}</span>
    </button>
    {#if tabMenue.temporaer}
      <!-- Nur fuer Vorschau-Tabs: ein angehefteter Tab kann nicht noch
           einmal angeheftet werden. Bisher ging das nur per Doppelklick
           auf den Tab -- eine Geste, die man kennen musste. -->
      <hr class="border-border my-1" />
      <button
        type="button"
        role="menuitem"
        class="menuepunkt"
        onclick={() => ausMenue(() => hefteAn(tabMenue!.gruppe, tabMenue!.id))}
      >
        <span>{t("shell.tabmenu.pin", "Anheften")}</span>
        <kbd class="text-fg-subtle ml-auto t-meta"
          >{t("shell.tabmenu.pin_hint", "Doppelklick")}</kbd
        >
      </button>
    {/if}
  </div>
{/if}

<style>
  /* Menuepunkt: eine Zeile im Tab-Kontextmenue. Ausgegraut heisst hier
     "waere wirkungslos" (nichts rechts davon, kein anderer Tab) -- der
     Punkt bleibt sichtbar, damit die Liste ihre Ordnung behaelt. */
  .menuepunkt {
    display: flex;
    width: 100%;
    align-items: center;
    gap: 0.5rem;
    border-radius: 0.375rem;
    padding: 0.25rem 0.625rem;
    text-align: left;
    color: var(--fg-muted);
    font-size: var(--t-body-size, inherit);
  }
  .menuepunkt:hover:not(:disabled) {
    background: color-mix(in srgb, var(--bg-muted) 70%, transparent);
    color: var(--fg);
  }
  .menuepunkt:disabled {
    opacity: 0.45;
    cursor: default;
  }
  /* Kursiv = Vorschau, dasselbe Zeichen wie am Tab: die Liste soll
     dieselbe Sprache sprechen wie die Leiste, aus der sie kommt. */
  .menuepunkt.kursiv {
    font-style: italic;
  }

  .kasten {
    background: var(--bg-elev);
  }
  /* Die Tab-Leiste ist der Kopf des Kastens: eine Spur abgesetzt, damit
     man sieht, wo der Inhalt beginnt. */
  .tabstrip {
    flex: none;
    /* Ganze Pixel -- siehe .crumbs. */
    min-height: 2.2rem;
    min-height: round(up, 2.2rem, 1px);
    /* Dieselbe Flaeche wie Seitenleiste und Panel: die Leiste ist
       Chrome, und der AKTIVE Tab ist die einzige Stelle, die auf die
       Editor-Flaeche hochsteigt -- so unterscheidet auch VS Code
       (Leiste #F8F8F8, aktiver Tab weiss). Vorher lag sie auf
       --bg-muted und blieb damit beim alten, dunklen Ton stehen,
       waehrend Grund und Kaesten schon aufgehellt waren (Tim 16.08.). */
    background: var(--shell-kasten, var(--bg-muted));
  }
  .tabs {
    overflow-x: auto;
    scrollbar-width: none;
  }
  /* Die Linie unter der Leiste läuft UNTER dem aktiven Tab durch: er deckt
     sie ab und geht nahtlos in die Fläche darunter über (Tim 18.10.2026:
     „Ziel ist, den Tab, der geöffnet ist, optisch gleich zu erfassen").
     Darum keine border-bottom an der Leiste, sondern eine Linie, über der
     der aktive Tab liegt. */
  .tabstrip {
    position: relative;
  }
  .tabstrip::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 1px;
    background: var(--border);
    pointer-events: none;
  }
  .tab.active {
    position: relative;
    z-index: 1;
  }
  .kante {
    z-index: 2;
  }
  .tabs::-webkit-scrollbar {
    display: none;
  }
  /* Die Verlaufskante zeigt, dass an dieser Seite noch etwas liegt.
     26px, weil sie den letzten Tab anfaedeln und nicht verdecken soll --
     und sie kostet keine Breite, anders als ein Pfeil. */
  .kante {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 26px;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.15s ease;
  }
  .kante.an {
    opacity: 1;
  }
  .kante.links {
    left: 0;
    background: linear-gradient(90deg, var(--shell-kasten, var(--bg-muted)), transparent);
  }
  .kante.rechts {
    right: 0;
    background: linear-gradient(270deg, var(--shell-kasten, var(--bg-muted)), transparent);
  }
  @media (prefers-reduced-motion: reduce) {
    .kante {
      transition: none;
    }
  }
  .tab {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.35rem 0.5rem 0.35rem 0.7rem;
    font-size: 0.8rem;
    color: var(--fg-subtle);
    border-right: 1px solid var(--border);
    border-top: 2px solid transparent;
    cursor: pointer;
    white-space: nowrap;
    max-width: 16rem;
    user-select: none;
    /* Erste Stufe gegen Enge: die Tabs geben Breite ab, statt sofort
       aus dem Bild zu laufen. Unter 7,5rem bleibt vom Titel nichts
       Unterscheidbares uebrig -- Symbol, Polsterung und Kreuz essen den
       Rest --, deshalb ist dort Schluss und die Leiste scrollt. */
    flex: 0 1 auto;
    min-width: 7.5rem;
  }
  /* Der Titel nimmt den freien Platz ein, damit das Kreuz an der RECHTEN
     Kante sitzt und nicht am Wortende klebt. Seit die Tabs eine
     Mindestbreite haben, sind sie bei kurzen Titeln ("Tasks") breiter
     als ihr Inhalt -- ohne das wandert das Kreuz mit der Titellaenge
     hin und her, und die Leiste hat keine Kante mehr, auf die man
     zielen kann (Tim 29.08.). */
  .tab .titel {
    flex: 1 1 auto;
    min-width: 0;
  }
  /* Symbol und Kreuz geben nichts ab: schrumpfen darf nur der Titel. */
  .tab :global(svg) {
    flex: none;
  }
  /* Beginn eines neuen Bereichs: eine schmale Fuge, damit die Blöcke
     auch ohne farbige Symbole (Einstellungen › Farbe) lesbar sind. */
  .tab.blockanfang {
    margin-left: 5px;
    border-left: 1px solid var(--border);
  }
  .tab:hover {
    color: var(--fg-muted);
  }
  .tab.active {
    background: var(--bg-elev);
    color: var(--fg);
  }
  /* Kursiv = Vorschau. Dasselbe Zeichen wie in VS Code, und es kostet
     keine Breite -- ein zusaetzliches Symbol im Tab waere teurer als
     die Information wert ist. */
  .tab.vorschau {
    font-style: italic;
  }
  .tab.zieht {
    opacity: 0.4;
  }
  .einfuegemarke {
    width: 2px;
    flex: none;
    background: var(--accent);
    margin: 0.2rem 0;
    border-radius: 1px;
  }
  .teilen-zone {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 28%;
    display: grid;
    place-items: center;
    z-index: 20;
    border-left: 2px dashed var(--border-strong);
    background: color-mix(in srgb, var(--accent) 6%, transparent);
    pointer-events: auto;
  }
  .teilen-zone.aktiv {
    border-left-color: var(--accent);
    background: color-mix(in srgb, var(--accent) 16%, transparent);
  }
  .zone-text {
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--fg-muted);
    background: var(--bg-elev);
    border: 1px solid var(--border-strong);
    border-radius: 999px;
    padding: 0.25rem 0.7rem;
  }
  /* Nur der Tab der FOKUSSIERTEN Spalte traegt den Akzent -- sonst
     sieht man bei geteilter Ansicht nicht, wer die Tastatur hat. */
  .tab.fokussiert {
    border-top-color: var(--marke);
  }
  /* „Bereichsfarbe als Ort": das Symbol des aktiven Tabs trägt sie mit.
     Mit „Farbige Symbole › Tabs" tun es alle Tabs (.tab-symbol, app.css). */
  :global(html[data-farbe-ort="1"]) .tab.active :global(.tab-symbol) {
    color: var(--bereich);
  }
  /* Das Kreuz: 16 px Zeichen (lib/kreuz.ts, wie überall) in einer Fläche
     von 1,5 rem (Tim 03.10.2026: „die Schließen-X was größer machen" --
     vorher 13 px in 1,05 rem, kaum zu treffen). Der negative Rand hält die Tab-Leiste so hoch wie
     bisher: die Fläche darf in die Polsterung des Tabs ragen. */
  /* Arbeitet auf der Seite etwas (lib/laeuft.svelte.ts: Claude, Drucker,
     Probe), trägt ihr Tab den Punkt des Baums (TreeNode .kurz-punkt):
     Akzent und leiser Puls, solange es läuft; gelb und still, wenn es auf
     dich wartet (Tim 25.10.2026: „wenn in einem Tab etwas arbeitet, wie
     Claude, dann sollte das noch an dem Tab ersichtlich sein"). Er steht
     vor dem Kreuz, damit er auch im aktiven Tab zu sehen ist. */
  .tab-lauf {
    width: 0.45rem;
    height: 0.45rem;
    border-radius: 50%;
    flex: none;
  }
  .tab-lauf.laeuft {
    background: var(--color-laeuft);
    animation: laeuft-puls 1.8s ease-in-out infinite;
  }
  .tab-lauf.warn {
    background: var(--warning);
  }
  @media (prefers-reduced-motion: reduce) {
    .tab-lauf.laeuft {
      animation: none;
    }
  }
  .tab-x {
    display: grid;
    place-items: center;
    width: 1.5rem;
    height: 1.5rem;
    margin: -0.25rem -0.2rem -0.25rem 0;
    border-radius: 5px;
    color: var(--fg-subtle);
    opacity: 0;
    flex: none;
  }
  .tab:hover .tab-x,
  .tab.active .tab-x {
    opacity: 1;
  }
  .tab-x:hover {
    background: var(--bg-muted);
    color: var(--fg);
  }
  .strip-btn {
    display: grid;
    grid-auto-flow: column;
    align-items: center;
    justify-content: center;
    gap: 0.2rem;
    padding: 0 0.55rem;
    color: var(--fg-subtle);
    flex: none;
  }
  .strip-btn .zahl {
    font-size: 0.75rem;
    font-weight: 600;
  }
  .strip-btn:hover {
    background: var(--bg-elev);
    color: var(--fg);
  }
  /* Der Scroll-Container traegt KEINE Polsterung -- die sitzt eine Ebene
     tiefer. Grund: ein sticky Element parkt an der Kante der Polsterbox
     des Scrollports, nicht an dessen Aussenkante. Mit padding-top hier
     klebten die Tabellenkoepfe 11px zu tief, und in diesem Streifen
     scrollten Zeilen sichtbar hinter ihnen durch (Tim 15.08.). */
  .inhalt {
    /* Oberkante fuer sticky Tabellenkoepfe: hier IST der Scroll-Container,
       ueber der Flaeche schwebt nichts (docs/tables.md). */
    --tabellen-top: 0px;
  }
  /* Ein gehaltener Tab, der nicht vorn steht: liegt unter dem vorderen,
     ohne Layout und Zeichnen (content-visibility), nicht erreichbar
     (inert im Markup). Sein Rollstand bleibt. */
  .inhalt.verdeckt {
    position: absolute;
    inset: 0;
    visibility: hidden;
    content-visibility: hidden;
    pointer-events: none;
  }
  .inhalt-innen {
    padding: 0.75rem max(1rem, env(safe-area-inset-right)) max(1rem, env(safe-area-inset-bottom))
      max(1rem, env(safe-area-inset-left));
    /* Oben ganze Pixel, aus demselben Safari-Grund wie bei .crumbs: die
       erste Karte der Seite (Backups: "forge") lag sonst bei 11.25px, und
       ihr geparkter Kopf liess nach dem Neuladen wieder den Streifen
       Zeilen durch (Tim 21.09.). */
    padding-top: round(0.75rem, 1px);
  }
  /* Tab-Leiste und Pfadzeile muessen GANZE Pixel hoch sein: sie
     bestimmen, wo die .inhalt beginnt. Lag deren Oberkante krumm (0.72rem
     Schrift + py-1 ergaben 24.375px, Oberkante 64.375), schnitt Safari
     (Mac) die geparkten Tabellen-Titelzeilen ein paar Pixel UNTER der
     Oberkante ab -- in dem Streifen darueber liefen die Zeilen sichtbar
     durch, in allen Tabellen (Tim 21.09.). Layout war buendig; in Safari
     nachgewiesen per margin-top auf die naechste ganze Zahl: Streifen weg.
     Chromium und Linux-WebKit sind nicht betroffen.
     Darum Zeilenhoehe und Polster per round() auf ganze Pixel -- das
     haelt auch bei anderer Grundschrift und --ui-density, und bei
     Umbruch auf zwei Zeilen. */
  .crumbs {
    flex: none;
    font-size: 0.72rem;
    /* Je zuerst der Rueckfall fuer Browser ohne round(). */
    line-height: 1.5;
    line-height: round(1.5em, 1px);
    padding-block: var(--spacing);
    padding-block: round(var(--spacing), 1px);
  }
  .crumb:hover {
    color: var(--fg);
    text-decoration: underline;
  }
</style>
